import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Loader2, ArrowLeft, Plus, Trash2, Save, CheckCircle2, AlertTriangle,
  Calculator, Grid as GridIcon, Ruler, Package, Wrench, Eye, EyeOff,
} from 'lucide-react';

/**
 * PurlinCalculatorPage — standalone planner reachable from the Reroof
 * Builder's Framing Detail block. Walks the operator through:
 *
 *   1. Per-face inputs (face length × face width → rows from face_length
 *      ÷ purlin_span; purlin lines run parallel to the eave at face_width)
 *   2. Optional hip / ridge / valley line lengths (each is a separate
 *      purlin line counted at length × 1, not ×2 — the operator already
 *      separately models the two sides via Face inputs)
 *   3. Blue purlin SCREW calculation per the office's rule sheet:
 *        • Face purlin: 2 screws per (purlin × rafter) junction
 *          + 2 start + 2 end for EACH purlin line in the face
 *        • Ridge / Valley / Hip line: 2 screws per (purlin-span)
 *          interval along the line + 2 start + 2 end
 *        • Perimeter (eave) line: 2 screws per (rafter-span)
 *          junction + 2 starting end
 *   4. Stock-length picker (4.8 m vs 6.0 m vs combo, with offcut %)
 *   5. Final ordered quantity per stock length
 *
 * Persisted on the parent reroof_spec under `purlin_calc`. Optionally
 * commits the accurate total back to `purlin_total_m_override` so the
 * BOM uses the operator-confirmed number.
 */

const RAFTER_SPAN_OPTIONS = [600, 750, 900, 1200];
const PURLIN_SPAN_OPTIONS = [600, 750, 900, 1200];

const DEFAULT_FACE = () => ({
  id:                Math.random().toString(36).slice(2),
  name:              'Face',
  face_length_m:     6,
  face_width_m:      10,
  rafter_span_mm:    900,
  purlin_span_mm:    900,
  accepted_rows_override: null,
});

const DEFAULT_STATE = {
  faces:              [],
  ridge_total_m:      0,
  valley_total_m:     0,
  hip_total_m:        0,
  perimeter_m:        0,
  perimeter_rafter_span_mm: 900,
  offcut_pct:         7,
  chosen_strategy:    '6.0',  // '4.8' | '6.0' | 'combo'
  combo_split_6m_pct: 60,
};

function calcFace(face) {
  const len   = Number(face.face_length_m || 0);   // up-slope (rafter direction)
  const width = Number(face.face_width_m || 0);    // along eave (purlin length)
  const ps_m  = Number(face.purlin_span_mm || 900) / 1000;
  const rs_m  = Number(face.rafter_span_mm || 900) / 1000;
  const face_m2 = +(len * width).toFixed(2);

  // Purlin row count: one row at the gutter + one every purlin-span
  // up the face, rounded up so we never under-buy.
  const calculated_rows = ps_m > 0 ? len / ps_m : 0;
  const default_rows = Math.max(1, Math.ceil(calculated_rows) + 1);
  const accepted_rows = face.accepted_rows_override != null
    ? Number(face.accepted_rows_override)
    : default_rows;
  const actual_spacing_mm = accepted_rows > 1 ? (len * 1000) / (accepted_rows - 1) : len * 1000;

  // Each purlin runs the face_width (along the eave).
  const face_subtotal_m = +(accepted_rows * width).toFixed(2);

  // Blue purlin SCREWS — per the office rule sheet:
  //   For EACH purlin row in this face:
  //     interior_junctions = ⌈face_width / rafter_span⌉ − 1
  //     screws_this_row    = 2 × interior_junctions + 2 (start) + 2 (end)
  //   Total face screws = rows × screws_this_row
  const junctions_along_purlin =
    rs_m > 0 ? Math.max(0, Math.ceil(width / rs_m) - 1) : 0;
  const screws_per_row = 2 * junctions_along_purlin + 2 + 2;
  const face_screws = accepted_rows * screws_per_row;

  return {
    ...face,
    face_m2,
    span_m: ps_m,
    rafter_span_m: rs_m,
    calculated_rows: +calculated_rows.toFixed(2),
    default_rows,
    accepted_rows,
    actual_spacing_mm: +actual_spacing_mm.toFixed(0),
    face_subtotal_m,
    junctions_along_purlin,
    screws_per_row,
    face_screws,
  };
}

function calcLineScrews(length_m, span_mm) {
  // Generic "purlin runs along this line" screw calc — used for
  // ridge / valley / hip lines. 2 screws per interior purlin-span
  // junction + 2 start + 2 end.
  const sm = Number(span_mm || 0) / 1000;
  if (length_m <= 0 || sm <= 0) return 0;
  const interior = Math.max(0, Math.ceil(length_m / sm) - 1);
  return 2 * interior + 2 + 2;
}

// Build the human-readable breakdown for a generic line screw calc —
// used by the shadowed-formula lines in the UI.
function explainLineScrews(length_m, span_mm) {
  const sm = Number(span_mm || 0) / 1000;
  if (length_m <= 0 || sm <= 0) return '0 (no length or span)';
  const interior = Math.max(0, Math.ceil(length_m / sm) - 1);
  const total = 2 * interior + 4;
  return `⌈${length_m.toFixed(2)} ÷ ${sm.toFixed(2)}⌉ − 1 = ${interior} junctions · 2×${interior} + 2 start + 2 end = ${total}`;
}

function explainPerimeterScrews(length_m, rafter_span_mm) {
  const rs = Number(rafter_span_mm || 0) / 1000;
  if (length_m <= 0 || rs <= 0) return '0 (no length or span)';
  const junctions = Math.ceil(length_m / rs);
  const total = 2 * junctions + 2;
  return `⌈${length_m.toFixed(2)} ÷ ${rs.toFixed(2)}⌉ = ${junctions} junctions · 2×${junctions} + 2 start = ${total}`;
}

function calcPerimeterScrews(length_m, rafter_span_mm) {
  // Perimeter eave line — 2 screws per rafter-junction + 2 for the
  // starting end (no separate end-of-perimeter count: perimeter
  // closes on itself).
  const rs = Number(rafter_span_mm || 0) / 1000;
  if (length_m <= 0 || rs <= 0) return 0;
  const junctions = Math.ceil(length_m / rs);
  return 2 * junctions + 2;
}

function calcOrder(accurate_total_m, strategy, combo6Pct, offcutPct) {
  const off = Math.max(0, Number(offcutPct || 0)) / 100;
  const required_m = accurate_total_m * (1 + off);
  const order = [];
  if (strategy === '4.8') {
    const qty = Math.ceil(required_m / 4.8);
    order.push({ len_m: 4.8, qty, total_m: +(qty * 4.8).toFixed(2) });
  } else if (strategy === '6.0') {
    const qty = Math.ceil(required_m / 6.0);
    order.push({ len_m: 6.0, qty, total_m: +(qty * 6.0).toFixed(2) });
  } else {
    const pct6 = Math.max(0, Math.min(100, Number(combo6Pct))) / 100;
    const for_6 = required_m * pct6;
    const for_48 = required_m * (1 - pct6);
    const qty6 = Math.ceil(for_6 / 6.0);
    const qty48 = Math.ceil(for_48 / 4.8);
    if (qty6 > 0) order.push({ len_m: 6.0, qty: qty6, total_m: +(qty6 * 6.0).toFixed(2) });
    if (qty48 > 0) order.push({ len_m: 4.8, qty: qty48, total_m: +(qty48 * 4.8).toFixed(2) });
  }
  const ordered_total_m = +order.reduce((a, x) => a + x.total_m, 0).toFixed(2);
  const surplus_m = +(ordered_total_m - accurate_total_m).toFixed(2);
  return { required_m: +required_m.toFixed(2), order, ordered_total_m, surplus_m };
}

export default function PurlinCalculatorPage() {
  const { id: appraisalId, specId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [spec, setSpec] = useState(null);
  const [state, setState] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [showCalcs, setShowCalcs] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/appraisals/${appraisalId}/reroof-specs`).then((r) => {
      if (!alive) return;
      const found = (r.data || []).find((s) => s._id === specId);
      if (!found) {
        setFlash({ tone: 'error', msg: 'Spec not found.' });
        return;
      }
      setSpec(found);
      const seeded = found.purlin_calc;
      if (seeded && seeded.faces && seeded.faces.length > 0) {
        setState({ ...DEFAULT_STATE, ...seeded });
      } else {
        const f = DEFAULT_FACE();
        f.name = 'Face A (whole roof)';
        f.purlin_span_mm = Number(found.purlin_span_mm || found.purlin_centres_mm || 900);
        f.rafter_span_mm = Number(found.rafter_span_mm || 900);
        const peri = Number(found.perimeter_m || 0);
        const m2 = Number(found.roof_m2 || 0);
        if (peri > 0 && m2 > 0) {
          f.face_width_m = +(peri / 2).toFixed(1);
          f.face_length_m = +(m2 / f.face_width_m).toFixed(2);
        }
        setState({
          ...DEFAULT_STATE,
          faces: [f],
          ridge_total_m:  Number(found.ridge_total_m  || 0),
          valley_total_m: Number(found.valley_total_m || 0),
          hip_total_m:    Number(found.hip_total_m    || 0),
          perimeter_m:    Number(found.perimeter_m    || 0),
          perimeter_rafter_span_mm: Number(found.rafter_span_mm || 900),
        });
      }
    }).catch((e) => {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [api, appraisalId, specId]);

  // ── Derivations ─────────────────────────────────────────────────────
  const facesCalc = useMemo(() => state.faces.map(calcFace), [state.faces]);
  const faces_subtotal_m = useMemo(
    () => +facesCalc.reduce((a, f) => a + f.face_subtotal_m, 0).toFixed(2),
    [facesCalc]
  );
  const ridge_purlin_m  = +(Number(state.ridge_total_m  || 0)).toFixed(2);
  const valley_purlin_m = +(Number(state.valley_total_m || 0)).toFixed(2);
  const hip_purlin_m    = +(Number(state.hip_total_m    || 0)).toFixed(2);
  const accurate_total_m = +(faces_subtotal_m + ridge_purlin_m + valley_purlin_m + hip_purlin_m).toFixed(2);

  // Blue purlin screws — pick the dominant purlin span for ridge/
  // valley/hip lines (operator usually uses one consistent value).
  const dominant_purlin_span_mm = state.faces[0]?.purlin_span_mm || 900;
  const face_screws_total   = facesCalc.reduce((a, f) => a + f.face_screws, 0);
  const ridge_screws_total  = calcLineScrews(ridge_purlin_m,  dominant_purlin_span_mm);
  const valley_screws_total = calcLineScrews(valley_purlin_m, dominant_purlin_span_mm);
  const hip_screws_total    = calcLineScrews(hip_purlin_m,    dominant_purlin_span_mm);
  const perimeter_screws_total = calcPerimeterScrews(
    Number(state.perimeter_m || 0),
    state.perimeter_rafter_span_mm,
  );
  const blue_screws_total = face_screws_total + ridge_screws_total + valley_screws_total + hip_screws_total + perimeter_screws_total;

  const orderCalc = useMemo(() =>
    calcOrder(accurate_total_m, state.chosen_strategy, state.combo_split_6m_pct, state.offcut_pct),
    [accurate_total_m, state.chosen_strategy, state.combo_split_6m_pct, state.offcut_pct]
  );

  const updateFace = useCallback((id, patch) => {
    setState((s) => ({ ...s, faces: s.faces.map((f) => f.id === id ? { ...f, ...patch } : f) }));
  }, []);
  const addFace = () => setState((s) => ({ ...s, faces: [...s.faces, { ...DEFAULT_FACE(), name: `Face ${String.fromCharCode(65 + s.faces.length)}` }] }));
  const removeFace = (id) => setState((s) => ({ ...s, faces: s.faces.filter((f) => f.id !== id) }));

  const save = async (commitOverride) => {
    setSaving(true);
    setFlash(null);
    const payload = {
      purlin_calc: {
        ...state,
        faces: facesCalc.map((f) => ({
          id: f.id, name: f.name,
          face_length_m: f.face_length_m,
          face_width_m: f.face_width_m,
          rafter_span_mm: f.rafter_span_mm,
          purlin_span_mm: f.purlin_span_mm,
          accepted_rows_override: f.accepted_rows_override,
          face_m2: f.face_m2,
          calculated_rows: f.calculated_rows,
          accepted_rows: f.accepted_rows,
          actual_spacing_mm: f.actual_spacing_mm,
          face_subtotal_m: f.face_subtotal_m,
          face_screws: f.face_screws,
        })),
        accurate_total_m,
        blue_screws: {
          face_total:      face_screws_total,
          ridge_total:     ridge_screws_total,
          valley_total:    valley_screws_total,
          hip_total:       hip_screws_total,
          perimeter_total: perimeter_screws_total,
          grand_total:     blue_screws_total,
        },
        order: orderCalc.order,
        ordered_total_m: orderCalc.ordered_total_m,
        surplus_m: orderCalc.surplus_m,
        updated_at: new Date().toISOString(),
      },
    };
    if (commitOverride) {
      payload.purlin_total_m_override = accurate_total_m;
    }
    try {
      await api.put(`/appraisals/${appraisalId}/reroof-specs/${specId}`, payload);
      setFlash({ tone: 'ok', msg: commitOverride
        ? `Saved ✓ · BOM override set to ${accurate_total_m} m · ${blue_screws_total.toLocaleString()} blue screws`
        : `Saved ✓ · ${blue_screws_total.toLocaleString()} blue screws calculated` });
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-sm text-slate-500"><Loader2 size={14} className="inline animate-spin mr-1" /> Loading purlin calculator…</div>;
  }
  if (!spec) {
    return <div className="p-8 text-sm text-rose-700">Spec not found.</div>;
  }

  return (
    <div className="space-y-5 max-w-7xl mx-auto" data-testid="purlin-calculator-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <button
            data-testid="purlin-calc-back"
            onClick={() => navigate(`/appraisals/${appraisalId}`)}
            className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-1"
          >
            <ArrowLeft size={12} /> Back to Re-roof Builder
          </button>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 font-[Manrope] flex items-center gap-2">
            <Calculator size={22} className="text-orange-600" /> Purlin Calculator
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Spec: <strong>{spec.name}</strong> · Roof profile: {spec.profile} · {spec.reroof_type}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="purlin-calc-toggle-calcs"
            onClick={() => setShowCalcs((v) => !v)}
            className="bg-white border border-slate-300 text-slate-700 text-xs font-semibold rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1"
            title="Show or hide the calculation breakdown shadow lines under each total"
          >
            {showCalcs ? <EyeOff size={13} /> : <Eye size={13} />} {showCalcs ? 'Hide calcs' : 'Show calcs'}
          </button>
          <button
            data-testid="purlin-calc-save"
            onClick={() => save(false)}
            disabled={saving}
            className="bg-white border border-slate-300 text-slate-700 text-sm font-semibold rounded-md px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
          </button>
          <button
            data-testid="purlin-calc-commit"
            onClick={() => save(true)}
            disabled={saving}
            className="bg-orange-600 text-white text-sm font-semibold rounded-md px-3 py-1.5 hover:bg-orange-700 disabled:opacity-50 inline-flex items-center gap-1"
          >
            <CheckCircle2 size={13} /> Save &amp; commit to BOM
          </button>
        </div>
      </div>

      {flash && (
        <div data-testid="purlin-calc-flash" className={`text-sm rounded-md p-3 border ${flash.tone === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {flash.tone === 'ok' ? <CheckCircle2 size={14} className="inline mr-1" /> : <AlertTriangle size={14} className="inline mr-1" />}
          {flash.msg}
        </div>
      )}

      {/* FACES */}
      <section className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-800 inline-flex items-center gap-2">
            <GridIcon size={14} className="text-orange-500" /> Per-face inputs
          </h2>
          <button
            data-testid="purlin-calc-add-face"
            onClick={addFace}
            className="text-xs bg-orange-50 border border-orange-200 text-orange-700 hover:bg-orange-100 rounded-md px-2 py-1 font-semibold inline-flex items-center gap-1"
          >
            <Plus size={12} /> Add face
          </button>
        </div>
        {facesCalc.length === 0 && (
          <div className="text-xs text-slate-500 italic py-3">No faces yet. Add at least one face to begin.</div>
        )}
        <div className="space-y-3">
          {facesCalc.map((f) => (
            <FaceCard
              key={f.id}
              face={f}
              showCalcs={showCalcs}
              onChange={(patch) => updateFace(f.id, patch)}
              onRemove={() => removeFace(f.id)}
              removable={facesCalc.length > 1}
            />
          ))}
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-md px-3 py-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-orange-900 font-semibold">Face purlin subtotal</span>
            <span className="font-mono-num font-bold text-orange-900" data-testid="purlin-calc-faces-subtotal">{faces_subtotal_m} m</span>
          </div>
          <CalcLine show={showCalcs} testid="purlin-calc-faces-subtotal-calc">
            {facesCalc.map((f) => `${f.name}: ${f.accepted_rows} rows × ${f.face_width_m} m = ${f.face_subtotal_m} m`).join(' · ') || '—'}
          </CalcLine>
        </div>
      </section>

      {/* RIDGE / VALLEY / HIP */}
      <section className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
        <h2 className="text-sm font-bold text-slate-800 inline-flex items-center gap-2">
          <Ruler size={14} className="text-orange-500" /> Ridge · Valley · Hip lines
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <Lbl label="Ridge total (m)">
            <input type="number" min={0} step={0.1} value={state.ridge_total_m}
              data-testid="purlin-calc-ridge-input"
              onChange={(e) => setState((s) => ({ ...s, ridge_total_m: Number(e.target.value) }))}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono-num" />
            <CalcLine show={showCalcs} testid="purlin-calc-ridge-calc">
              Blue screws: {explainLineScrews(ridge_purlin_m, dominant_purlin_span_mm)} = <strong>{ridge_screws_total}</strong>
            </CalcLine>
          </Lbl>
          <Lbl label="Valley total (m)">
            <input type="number" min={0} step={0.1} value={state.valley_total_m}
              data-testid="purlin-calc-valley-input"
              onChange={(e) => setState((s) => ({ ...s, valley_total_m: Number(e.target.value) }))}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono-num" />
            <CalcLine show={showCalcs} testid="purlin-calc-valley-calc">
              Blue screws: {explainLineScrews(valley_purlin_m, dominant_purlin_span_mm)} = <strong>{valley_screws_total}</strong>
            </CalcLine>
          </Lbl>
          <Lbl label="Hip total (m)">
            <input type="number" min={0} step={0.1} value={state.hip_total_m}
              data-testid="purlin-calc-hip-input"
              onChange={(e) => setState((s) => ({ ...s, hip_total_m: Number(e.target.value) }))}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono-num" />
            <CalcLine show={showCalcs} testid="purlin-calc-hip-calc">
              Blue screws: {explainLineScrews(hip_purlin_m, dominant_purlin_span_mm)} = <strong>{hip_screws_total}</strong>
            </CalcLine>
          </Lbl>
        </div>
      </section>

      {/* PERIMETER (for screw count only — purlin runs along gutter
          line, total length already in face_width sum). */}
      <section className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
        <h2 className="text-sm font-bold text-slate-800 inline-flex items-center gap-2">
          <Ruler size={14} className="text-orange-500" /> Perimeter (eave) — screw count only
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <Lbl label="Perimeter total (m)">
            <input type="number" min={0} step={0.1} value={state.perimeter_m}
              data-testid="purlin-calc-perimeter-input"
              onChange={(e) => setState((s) => ({ ...s, perimeter_m: Number(e.target.value) }))}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono-num" />
          </Lbl>
          <Lbl label="Rafter span (mm)">
            <SpanSelect
              testid="purlin-calc-perimeter-rafter-span"
              value={state.perimeter_rafter_span_mm}
              options={RAFTER_SPAN_OPTIONS}
              onChange={(v) => setState((s) => ({ ...s, perimeter_rafter_span_mm: v }))}
            />
          </Lbl>
          <Lbl label="Perimeter blue screws">
            <ReadOut val={perimeter_screws_total.toLocaleString()} testid="purlin-calc-perimeter-screws" />
            <CalcLine show={showCalcs} testid="purlin-calc-perimeter-calc">
              {explainPerimeterScrews(Number(state.perimeter_m || 0), state.perimeter_rafter_span_mm)}
            </CalcLine>
          </Lbl>
        </div>
      </section>

      {/* ACCURATE PURLIN TOTAL */}
      <section className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-300 rounded-md p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">Accurate purlin total</div>
            <div className="text-xs text-emerald-800/80 mt-0.5">
              {faces_subtotal_m} faces + {ridge_purlin_m} ridge + {valley_purlin_m} valley + {hip_purlin_m} hip
            </div>
          </div>
          <div className="text-3xl font-extrabold text-emerald-800 font-mono-num" data-testid="purlin-calc-accurate-total">
            {accurate_total_m} m
          </div>
        </div>
        <CalcLine show={showCalcs} testid="purlin-calc-accurate-total-calc" tone="emerald">
          Σ = {faces_subtotal_m} (faces) + {ridge_purlin_m} (ridge) + {valley_purlin_m} (valley) + {hip_purlin_m} (hip) = <strong>{accurate_total_m} m</strong>
        </CalcLine>
      </section>

      {/* BLUE PURLIN SCREW TOTAL */}
      <section className="bg-gradient-to-br from-sky-50 to-sky-100/50 border border-sky-300 rounded-md p-4" data-testid="purlin-calc-blue-screws-card">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-bold text-sky-800 inline-flex items-center gap-2">
            <Wrench size={14} className="text-sky-600" /> Blue purlin screws — total order
          </div>
          <div className="text-3xl font-extrabold text-sky-800 font-mono-num" data-testid="purlin-calc-blue-screws-total">
            {blue_screws_total.toLocaleString()}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          <ScrewPill label="Faces"     value={face_screws_total}      testid="purlin-calc-screws-faces" />
          <ScrewPill label="Ridge"     value={ridge_screws_total}     testid="purlin-calc-screws-ridge" />
          <ScrewPill label="Valley"    value={valley_screws_total}    testid="purlin-calc-screws-valley" />
          <ScrewPill label="Hip"       value={hip_screws_total}       testid="purlin-calc-screws-hip" />
          <ScrewPill label="Perimeter" value={perimeter_screws_total} testid="purlin-calc-screws-perimeter" />
        </div>
        <CalcLine show={showCalcs} testid="purlin-calc-blue-screws-total-calc" tone="sky">
          Σ = {face_screws_total} (faces) + {ridge_screws_total} (ridge) + {valley_screws_total} (valley) + {hip_screws_total} (hip) + {perimeter_screws_total} (perimeter) = <strong>{blue_screws_total.toLocaleString()}</strong>
        </CalcLine>
      </section>

      {/* STOCK LENGTHS + OFFCUT */}
      <section className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
        <h2 className="text-sm font-bold text-slate-800 inline-flex items-center gap-2">
          <Package size={14} className="text-orange-500" /> Stock lengths &amp; offcut
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <Lbl label="Strategy">
            <div className="flex gap-1">
              {['4.8', '6.0', 'combo'].map((s) => (
                <button
                  key={s}
                  data-testid={`purlin-calc-strategy-${s}`}
                  onClick={() => setState((st) => ({ ...st, chosen_strategy: s }))}
                  className={`text-xs font-bold px-2.5 py-1 rounded-md border ${state.chosen_strategy === s ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
                >
                  {s === 'combo' ? 'Combo' : `${s} m only`}
                </button>
              ))}
            </div>
          </Lbl>
          {state.chosen_strategy === 'combo' && (
            <Lbl label={`6 m share (${state.combo_split_6m_pct}%)`}>
              <input type="range" min={0} max={100} step={5}
                data-testid="purlin-calc-combo-slider"
                value={state.combo_split_6m_pct}
                onChange={(e) => setState((s) => ({ ...s, combo_split_6m_pct: Number(e.target.value) }))}
                className="w-full accent-orange-600" />
            </Lbl>
          )}
          <Lbl label="Offcut %">
            <input type="number" min={0} max={50} step={0.5}
              data-testid="purlin-calc-offcut"
              value={state.offcut_pct}
              onChange={(e) => setState((s) => ({ ...s, offcut_pct: Number(e.target.value) }))}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono-num" />
          </Lbl>
        </div>
      </section>

      {/* FINAL ORDER */}
      <section className="bg-white border border-slate-300 rounded-md overflow-hidden" data-testid="purlin-calc-order">
        <div className="bg-slate-900 text-white px-4 py-2 text-xs uppercase tracking-wide font-bold">
          Final order — purlin quantities
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left p-2 px-3">Stock length</th>
              <th className="text-right p-2 px-3">Qty</th>
              <th className="text-right p-2 px-3">Total m</th>
            </tr>
          </thead>
          <tbody>
            {orderCalc.order.map((o) => (
              <tr key={o.len_m} data-testid={`purlin-calc-order-row-${o.len_m}`} className="border-t border-slate-100">
                <td className="p-2 px-3 font-semibold">{o.len_m} m purlins</td>
                <td className="p-2 px-3 text-right font-mono-num">{o.qty}</td>
                <td className="p-2 px-3 text-right font-mono-num">{o.total_m}</td>
              </tr>
            ))}
            <tr className="bg-orange-50 border-t-2 border-orange-200 font-bold">
              <td className="p-2 px-3 text-orange-900">Required (incl. {state.offcut_pct}% offcut)</td>
              <td />
              <td className="p-2 px-3 text-right text-orange-900 font-mono-num">{orderCalc.required_m} m</td>
            </tr>
            <tr className="bg-emerald-50 font-bold">
              <td className="p-2 px-3 text-emerald-900">Ordered total</td>
              <td />
              <td className="p-2 px-3 text-right text-emerald-900 font-mono-num" data-testid="purlin-calc-ordered-total">{orderCalc.ordered_total_m} m</td>
            </tr>
            <tr className={`${orderCalc.surplus_m >= 0 ? 'text-emerald-700' : 'text-rose-700'} text-xs`}>
              <td className="p-2 px-3 italic">Surplus vs required ({orderCalc.surplus_m >= 0 ? 'enough' : 'short'})</td>
              <td />
              <td className="p-2 px-3 text-right font-mono-num">{orderCalc.surplus_m} m</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  );
}

// ── Subcomponents ────────────────────────────────────────────────────
function FaceCard({ face, onChange, onRemove, removable, showCalcs }) {
  const GRID_W = 220;
  const GRID_H = 80;
  const rows = Math.max(2, Math.min(40, Number(face.accepted_rows || 2)));
  const lines = [];
  for (let i = 0; i < rows; i++) {
    const y = (i / (rows - 1)) * GRID_H;
    lines.push(<line key={`p${i}`} x1={0} y1={y} x2={GRID_W} y2={y} stroke="#9CA3AF" strokeWidth={i === 0 ? 1.6 : 0.8} />);
  }
  for (let i = 1; i < 10; i++) {
    const x = (i / 10) * GRID_W;
    lines.push(<line key={`r${i}`} x1={x} y1={0} x2={x} y2={GRID_H} stroke="#D1D5DB" strokeWidth={0.4} strokeDasharray="2 2" />);
  }
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2" data-testid={`purlin-calc-face-${face.id}`}>
      <div className="flex items-center justify-between gap-2">
        <input
          value={face.name}
          data-testid={`purlin-calc-face-name-${face.id}`}
          onChange={(e) => onChange({ name: e.target.value })}
          className="text-sm font-bold text-slate-800 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-orange-500 focus:outline-none px-1"
        />
        {removable && (
          <button onClick={onRemove} className="text-rose-500 hover:text-rose-700" data-testid={`purlin-calc-face-remove-${face.id}`}>
            <Trash2 size={13} />
          </button>
        )}
      </div>
      <div className="flex gap-3 flex-wrap">
        <div className="flex-shrink-0">
          <svg width={GRID_W} height={GRID_H} className="bg-white border border-slate-200 rounded">
            <rect x={0} y={0} width={GRID_W} height={GRID_H} fill="white" />
            {lines}
            <line x1={0} y1={GRID_H} x2={GRID_W} y2={GRID_H} stroke="#0F4F1B" strokeWidth={2} />
            <text x={GRID_W / 2} y={GRID_H - 4} textAnchor="middle" fontSize="8" fill="#0F4F1B" fontWeight="bold">gutter</text>
          </svg>
          <div className="text-[10px] text-slate-500 text-center mt-0.5">
            {face.accepted_rows} purlin rows @ ≈{face.actual_spacing_mm} mm
          </div>
        </div>
        <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm min-w-[280px]">
          <Lbl label="Face length / up-slope (m)">
            <input type="number" min={0} step={0.1} value={face.face_length_m}
              data-testid={`purlin-calc-face-length-${face.id}`}
              onChange={(e) => onChange({ face_length_m: Number(e.target.value) })}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono-num" />
          </Lbl>
          <Lbl label="Face width / along gutter (m)">
            <input type="number" min={0} step={0.1} value={face.face_width_m}
              data-testid={`purlin-calc-face-width-${face.id}`}
              onChange={(e) => onChange({ face_width_m: Number(e.target.value) })}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono-num" />
          </Lbl>
          <Lbl label="Rafter span (mm)">
            <SpanSelect
              testid={`purlin-calc-face-rafter-span-${face.id}`}
              value={face.rafter_span_mm}
              options={RAFTER_SPAN_OPTIONS}
              onChange={(v) => onChange({ rafter_span_mm: v })}
            />
          </Lbl>
          <Lbl label="Purlin span (mm)">
            <SpanSelect
              testid={`purlin-calc-face-purlin-span-${face.id}`}
              value={face.purlin_span_mm}
              options={PURLIN_SPAN_OPTIONS}
              onChange={(v) => onChange({ purlin_span_mm: v })}
            />
          </Lbl>
          <Lbl label={`Rows (calc ${face.calculated_rows} → default ${face.default_rows})`}>
            <input type="number" min={1} step={1}
              data-testid={`purlin-calc-face-accepted-rows-${face.id}`}
              value={face.accepted_rows}
              onChange={(e) => onChange({ accepted_rows_override: Number(e.target.value) })}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono-num" />
          </Lbl>
          <Lbl label="Face area">
            <ReadOut val={`${face.face_m2} m²`} testid={`purlin-calc-face-m2-${face.id}`} />
            <CalcLine show={showCalcs} testid={`purlin-calc-face-m2-calc-${face.id}`}>
              {face.face_length_m} × {face.face_width_m} = <strong>{face.face_m2} m²</strong>
            </CalcLine>
          </Lbl>
          <Lbl label="Face purlin subtotal">
            <ReadOut val={`${face.face_subtotal_m} m`} testid={`purlin-calc-face-subtotal-${face.id}`} highlight />
            <CalcLine show={showCalcs} testid={`purlin-calc-face-subtotal-calc-${face.id}`}>
              {face.accepted_rows} rows × {face.face_width_m} m (gutter run) = <strong>{face.face_subtotal_m} m</strong>
            </CalcLine>
          </Lbl>
          <Lbl label="Purlin × rafter junctions / row">
            <ReadOut val={face.junctions_along_purlin.toLocaleString()} testid={`purlin-calc-face-junctions-${face.id}`} />
            <CalcLine show={showCalcs} testid={`purlin-calc-face-junctions-calc-${face.id}`}>
              ⌈{face.face_width_m} ÷ {(face.rafter_span_m || 0).toFixed(2)}⌉ − 1 = <strong>{face.junctions_along_purlin}</strong> interior junctions
            </CalcLine>
          </Lbl>
          <Lbl label="Blue screws (this face)">
            <ReadOut val={face.face_screws.toLocaleString()} testid={`purlin-calc-face-screws-${face.id}`} />
            <CalcLine show={showCalcs} testid={`purlin-calc-face-screws-calc-${face.id}`}>
              {face.accepted_rows} rows × (2×{face.junctions_along_purlin} + 2 start + 2 end) = {face.accepted_rows} × {face.screws_per_row} = <strong>{face.face_screws.toLocaleString()}</strong>
            </CalcLine>
          </Lbl>
        </div>
      </div>
    </div>
  );
}

function SpanSelect({ value, options, onChange, testid }) {
  const v = Number(value || 0);
  const isCustom = !options.includes(v);
  return (
    <div className="flex gap-1">
      <select
        data-testid={testid}
        value={isCustom ? '__custom' : String(v)}
        onChange={(e) => {
          if (e.target.value === '__custom') return; // keep current value, switch to input
          onChange(Number(e.target.value));
        }}
        className="flex-1 px-2 py-1.5 text-sm border border-slate-300 rounded font-mono-num bg-white"
      >
        {options.map((o) => (
          <option key={o} value={String(o)}>{o} mm</option>
        ))}
        <option value="__custom">Custom…</option>
      </select>
      {isCustom && (
        <input
          type="number" min={100} max={3000} step={50}
          value={v}
          data-testid={`${testid}-custom`}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-20 px-1.5 py-1.5 text-xs border border-slate-300 rounded font-mono-num"
        />
      )}
    </div>
  );
}

function ScrewPill({ label, value, testid }) {
  return (
    <div className="bg-white rounded border border-sky-200 px-2 py-1.5 text-center" data-testid={testid}>
      <div className="text-[9px] uppercase font-bold tracking-wide text-sky-600/70">{label}</div>
      <div className="font-mono-num font-bold text-sky-900 text-sm mt-0.5">{value.toLocaleString()}</div>
    </div>
  );
}

function Lbl({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase font-bold tracking-wide text-slate-500">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

function ReadOut({ val, testid, highlight = false }) {
  return (
    <input
      type="text"
      readOnly
      value={val}
      data-testid={testid}
      className={`w-full px-2 py-1.5 text-sm rounded font-mono-num border border-dashed border-slate-300 cursor-default ${highlight ? 'bg-orange-50 text-orange-800 font-bold' : 'bg-slate-100 text-slate-700'}`}
    />
  );
}

// Shadowed formula breakdown — sits under any total/input to show the
// math the operator's eyes can verify. Suppressed when the global
// "Hide calcs" toggle is off so the calculator can run in "clean"
// presentation mode.
function CalcLine({ show, children, testid, tone = 'slate' }) {
  if (!show) return null;
  const toneCls =
    tone === 'emerald' ? 'text-emerald-700/70' :
    tone === 'sky'     ? 'text-sky-700/70'     :
                         'text-slate-500/80';
  return (
    <div
      data-testid={testid}
      className={`mt-0.5 text-[10px] font-mono-num italic leading-snug ${toneCls}`}
    >
      {children}
    </div>
  );
}
